import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { imgUrl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, PlusCircle, LogIn, UploadCloud, X, ImageIcon, Star, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { INDIA_STATES, STATE_DISTRICTS } from "@/data/indiaLocations";
import { VerifyDialog } from "@/components/VerifyDialog";

const TYPES = ["Apartment", "Villa", "Independent House", "Plot", "Commercial"];
const FURNISH = ["Unfurnished", "Semi-Furnished", "Furnished"];

export default function PostProperty() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState([]); // array of relative urls
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", property_type: "Apartment", listing_type: "buy",
    price: "", state: "Karnataka", city: "", locality: "", bhk: "2", bathrooms: "2",
    car_spaces: "1", ensuite: false, area_sqft: "", furnishing: "Unfurnished", amenities: "",
    contact_name: user?.name || "", contact_phone: "",
  });

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <div className="h-14 w-14 rounded-2xl bg-navy/10 flex items-center justify-center mx-auto">
          <LogIn className="h-7 w-7 text-navy" />
        </div>
        <h1 className="mt-5 font-display text-2xl font-semibold text-slate-900">Sign in to post a property</h1>
        <p className="mt-2 text-slate-500">List your property for free and connect with genuine buyers and tenants.</p>
        <Button data-testid="post-login-btn" onClick={login} className="mt-6 bg-navy hover:bg-navy-dark">Sign in with Google</Button>
      </div>
    );
  }

  if (!user.verified) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <div className="h-14 w-14 rounded-2xl bg-gold/20 flex items-center justify-center mx-auto">
          <ShieldAlert className="h-7 w-7 text-gold-dark" />
        </div>
        <h1 className="mt-5 font-display text-2xl font-semibold text-slate-900">Verify your identity to post</h1>
        <p className="mt-2 text-slate-500">To keep VYAPAAR trusted and spam-free, only KYC-verified owners and agents can list properties. It takes a minute.</p>
        <div className="mt-6">
          <VerifyDialog trigger={<Button data-testid="post-verify-btn" className="bg-navy hover:bg-navy-dark text-white">Verify Now</Button>} />
        </div>
      </div>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, res.data.url]);
        toast.success(`${file.name} uploaded (${res.data.width}×${res.data.height})`);
      } catch (err) {
        const msg = err?.response?.data?.detail || "Upload failed";
        toast.error(msg);
      }
    }
    setUploading(false);
  };

  const removeImage = (url) => setImages((prev) => prev.filter((u) => u !== url));

  const setCover = (url) => {
    setImages((prev) => [url, ...prev.filter((u) => u !== url)]);
    toast.success("Cover photo updated");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (images.length < 5) {
      toast.error("Please upload at least 5 high-definition photos.");
      return;
    }
    if (!form.city) {
      toast.error("Please select a district / city.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseInt(form.price, 10),
        bhk: parseInt(form.bhk, 10),
        bathrooms: parseInt(form.bathrooms, 10),
        car_spaces: parseInt(form.car_spaces, 10) || 0,
        ensuite: !!form.ensuite,
        area_sqft: parseInt(form.area_sqft, 10),
        amenities: form.amenities ? form.amenities.split(",").map((a) => a.trim()).filter(Boolean) : [],
        images,
      };
      const res = await api.post("/properties", payload);
      toast.success("Property listed successfully!");
      navigate(`/properties/${res.data.id}`);
    } catch {
      toast.error("Could not list property. Check all fields.");
    } finally {
      setSaving(false);
    }
  };

  const field = (label, key, props = {}) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input data-testid={`post-${key}`} value={form[key]} onChange={(e) => set(key, e.target.value)} {...props} />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-gold flex items-center justify-center"><PlusCircle className="h-6 w-6 text-navy" /></div>
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">Post a Property</h1>
          <p className="text-slate-500 text-sm">Fill in the details below to list your property.</p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        {/* Image upload */}
        <div>
          <Label className="text-xs">Property Photos</Label>
          <p className="text-xs text-slate-400 mb-2">Upload at least 5 clear, high-definition photos (min 1024×768px). JPEG/PNG/WebP, up to 15MB each. The first photo is the cover — click any photo to make it the cover.</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {images.map((url, idx) => (
              <div key={url} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group">
                <img src={imgUrl(url)} alt="" className="w-full h-full object-cover" />
                {idx === 0 ? (
                  <span data-testid="cover-badge" className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-navy text-white flex items-center gap-1">
                    <Star className="h-2.5 w-2.5 fill-white" /> Cover
                  </span>
                ) : (
                  <button
                    type="button"
                    data-testid={`set-cover-btn-${idx}`}
                    onClick={() => setCover(url)}
                    className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-black/60 text-white opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    Set cover
                  </button>
                )}
                <button
                  type="button"
                  data-testid="remove-image-btn"
                  onClick={() => removeImage(url)}
                  className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <label
              data-testid="upload-dropzone"
              className="aspect-square rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-navy hover:bg-navy/5/40 transition-colors text-slate-500"
            >
              {uploading ? (
                <Loader2 className="h-6 w-6 animate-spin text-navy" />
              ) : (
                <>
                  <UploadCloud className="h-6 w-6" />
                  <span className="text-[11px] mt-1 font-medium">Add photos</span>
                </>
              )}
              <input
                data-testid="post-image-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={handleFiles}
                className="hidden"
                disabled={uploading}
              />
            </label>
          </div>
          {images.length < 5 && (
            <p className="mt-2 text-xs text-slate-400 flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" /> {images.length}/5 photos — at least 5 high-definition photos are required.</p>
          )}
        </div>

        {field("Title", "title", { required: true, placeholder: "e.g. Spacious 3BHK in Whitefield" })}
        <div>
          <Label className="text-xs">Description</Label>
          <Textarea data-testid="post-description" rows={3} required value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Describe the property…" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Listing Type</Label>
            <Select value={form.listing_type} onValueChange={(v) => set("listing_type", v)}>
              <SelectTrigger data-testid="post-listing-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="buy">Sell</SelectItem><SelectItem value="rent">Rent</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Property Type</Label>
            <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
              <SelectTrigger data-testid="post-prop-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">State / UT</Label>
            <Select value={form.state} onValueChange={(v) => { set("state", v); set("city", ""); }}>
              <SelectTrigger data-testid="post-state"><SelectValue placeholder="Select state" /></SelectTrigger>
              <SelectContent className="max-h-72">{INDIA_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">District / City</Label>
            <Select value={form.city} onValueChange={(v) => set("city", v)}>
              <SelectTrigger data-testid="post-city"><SelectValue placeholder="Select district" /></SelectTrigger>
              <SelectContent className="max-h-72">{(STATE_DISTRICTS[form.state] || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {field("Town / Locality / Area", "locality", { required: true, placeholder: "e.g. Koramangala, Sector 62" })}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs">BHK</Label>
            <Select value={form.bhk} onValueChange={(v) => set("bhk", v)}>
              <SelectTrigger data-testid="post-bhk"><SelectValue /></SelectTrigger>
              <SelectContent>{["1","2","3","4","5"].map((b) => <SelectItem key={b} value={b}>{b} BHK</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {field("Bathrooms", "bathrooms", { type: "number", min: 1 })}
          {field("Car Spaces", "car_spaces", { type: "number", min: 0 })}
          {field("Area (sqft)", "area_sqft", { type: "number", required: true, placeholder: "1200" })}
        </div>

        <label data-testid="post-ensuite" className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={form.ensuite} onChange={(e) => set("ensuite", e.target.checked)} className="h-4 w-4 accent-[#0F3D91]" />
          <span className="text-sm text-slate-700">Has an ensuite bathroom (attached to bedroom)</span>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
          <div>
            <Label className="text-xs">Furnishing</Label>
            <Select value={form.furnishing} onValueChange={(v) => set("furnishing", v)}>
              <SelectTrigger data-testid="post-furnishing"><SelectValue /></SelectTrigger>
              <SelectContent>{FURNISH.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {field("Price (₹)", "price", { type: "number", required: true, placeholder: "For rent enter monthly rent" })}
        {field("Amenities (comma separated)", "amenities", { placeholder: "Gym, Parking, Pool" })}

        <div className="grid grid-cols-2 gap-4">
          {field("Contact Name", "contact_name", { required: true })}
          {field("Contact Phone", "contact_phone", { required: true, placeholder: "+91 …" })}
        </div>

        <Button data-testid="post-submit-btn" type="submit" disabled={saving} className="w-full bg-navy hover:bg-navy-dark text-white h-11">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "List Property"}
        </Button>
      </form>
    </div>
  );
}
